import React from 'react';
import PropTypes from 'prop-types';

import ArrowRight from '@decisiv/iconix/lib/components/ArrowRight';
import UserPlus from '@decisiv/iconix/lib/components/UserPlus';

import Button from '@decisiv/ui-components/lib/components/Button';
import { IconContainer } from '@decisiv/ui-components/lib/components/Link/styles';
import Message from '@decisiv/ui-components/lib/components/Message';

import { SUCCESS_PANEL } from './copy';
import { TerminalActions, TerminalPanel } from './styles';
import PlainLink from '../../../shared/PlainLink';
import allowDefaultActionInModal from '../../../shared/modalClick';
import { editUserPath } from '../../../shared/paths';

/**
 * Shown once the user exists — created, or an existing user
 * restored/added/updated (Figma 787:76642 for the create copy; the others
 * have no node yet, see CreateUserWizard/copy.js).
 *
 * The username is the one the server derived from the email — the wizard never
 * asked for it — so naming it here is the admin's only chance to see it.
 *
 * The welcome-email line is not in the mockup's placeholder copy but the ticket
 * requires it: the admin never sets a password, so they need to know the user is
 * contacted automatically. It only applies to a freshly created user — restoring
 * or adding an existing one sends no such mail.
 *
 * VIEW NEW USER goes to the tool's own edit page (PD-4596), never the legacy
 * form under the old portal chrome. Both of its oddities are load-bearing and
 * look deletable: `PlainLink` because KeyDS `Link` cannot survive a re-render
 * here, and `allowDefaultActionInModal` because the modal cancels the click's
 * default action, which for an anchor is the navigation itself.
 */
function SuccessPanel({ kind, onCreateAnother, skippedCount, user }) {
  const copy = SUCCESS_PANEL[kind];
  const extras = [
    copy.emailNotice,
    skippedCount > 0 ? SUCCESS_PANEL.partialCaveat(skippedCount) : null,
  ]
    .filter(Boolean)
    .join(' ');
  const body = extras
    ? `${copy.body(user.username)} ${extras}`
    : copy.body(user.username);

  return (
    <TerminalPanel>
      <Message
        intent="success"
        size="large"
        title={copy.title}
        variant="vertical"
      >
        {body}
      </Message>
      <TerminalActions>
        <PlainLink
          href={editUserPath(user.id)}
          onClick={allowDefaultActionInModal}
        >
          {SUCCESS_PANEL.viewUser}
          {/* KeyDS's own container, so the arrow aligns like every other link. */}
          <IconContainer aria-hidden position="right" withText>
            <ArrowRight size="medium" />
          </IconContainer>
        </PlainLink>
        <Button
          icon={UserPlus}
          kind="secondary"
          text={SUCCESS_PANEL.createAnother}
          onClick={onCreateAnother}
        />
      </TerminalActions>
    </TerminalPanel>
  );
}

SuccessPanel.propTypes = {
  kind: PropTypes.oneOf(['created', 'restored', 'added', 'updated']),
  /** Resets the wizard to a blank step 1. */
  onCreateAnother: PropTypes.func.isRequired,
  skippedCount: PropTypes.number,
  user: PropTypes.shape({
    id: PropTypes.number.isRequired,
    username: PropTypes.string,
  }).isRequired,
};

SuccessPanel.defaultProps = {
  kind: 'created',
  skippedCount: 0,
};

export default SuccessPanel;
